/**
 * The PAICE context layer schema, mirrored from the agents service.
 *
 * Source of truth is `ContextLayer` in the agents service's `models.py`. The
 * service validates with `extra="forbid"` at every level and reports failures
 * as flattened `domains.0.datasets.2.name` paths. Validating the same rules
 * here lets the settings page point at the offending field before a save round
 * trip, and — more importantly — lets Easy Mode refuse to open on a document it
 * would otherwise silently mangle.
 *
 * Keep this in step with the service: a rule that exists only here blocks a
 * document the service would accept, and one that exists only there turns into
 * a 400 the user cannot see coming.
 */
import YAML from 'yaml';

export const JOIN_RELATIONS = ['one_to_one', 'one_to_many', 'many_to_one', 'many_to_many'];

/** Matches MAX_CONTEXT_LAYER_BYTES on the agents service (Lambda caps bodies at 6 MB). */
export const MAX_CONTEXT_BYTES = 5 * 1024 * 1024;

/**
 * Allowed keys per node type. Anything outside these sets is what the service's
 * `extra="forbid"` rejects, so it is an error here too rather than a warning.
 */
const ALLOWED_KEYS = {
    root: ['version', 'domains'],
    domain: ['name', 'description', 'synonyms', 'datasets', 'metrics'],
    dataset: ['name', 'description', 'synonyms', 'fields', 'joins', 'tags'],
    field: ['name', 'synonyms', 'tags', 'value_groups'],
    valueGroup: ['business_term', 'synonyms', 'sql'],
    join: ['target_entity', 'relation', 'sql'],
    metric: ['name', 'description', 'synonyms', 'sql'],
};

export const STARTER_TEMPLATE = `# PAICE context layer
# Describes your own datasets so answers are grounded in your real data.
version: "1.0"
domains:
  - name: web_analytics
    description: Site traffic and engagement
    synonyms: [web, site]
    datasets:
      - name: pageviews
        description: One row per page view
        synonyms: [views]
        fields:
          - name: device_type
            synonyms: [device]
            tags: [dimension]
            value_groups:
              - business_term: mobile
                synonyms: [phone, handset]
                sql: "device_type IN ('phone', 'tablet')"
        joins:
          - target_entity: sessions
            relation: many_to_one
            sql: "pageviews.session_id = sessions.session_id"
    metrics:
      - name: total_pageviews
        description: Count of page views
        synonyms: [views, hits]
        sql: "COUNT(*)"
`;

/** Factory functions for the blank rows Easy Mode's "Add" buttons create. */
export const blankValueGroup = () => ({ business_term: '', synonyms: [], sql: '' });
export const blankField = () => ({ name: '', synonyms: [], tags: [], value_groups: [] });
export const blankJoin = () => ({ target_entity: '', relation: 'many_to_one', sql: '' });
export const blankMetric = () => ({ name: '', description: '', synonyms: [], sql: '' });
export const blankDataset = () => ({
    name: '', description: '', synonyms: [], fields: [], joins: [], tags: [],
});
export const blankDomain = () => ({
    name: '', description: '', synonyms: [], datasets: [], metrics: [],
});
export const blankLayer = () => ({ version: '1.0', domains: [blankDomain()] });

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Validate a parsed document against the service schema.
 *
 * @param {*} data - the result of parsing the YAML
 * @returns {Array<{path: string, message: string}>} one entry per violation,
 *          pathed the same way the service reports them
 */
export function validateContextLayer(data) {
    const errors = [];
    const add = (path, message) => errors.push({ path, message });

    if (!isPlainObject(data)) {
        add('<root>', "context layer must be a YAML mapping with 'version' and 'domains' keys");
        return errors;
    }

    /** Flags keys the service's `extra="forbid"` would reject. */
    const checkExtraKeys = (node, allowed, path) => {
        Object.keys(node).forEach((key) => {
            if (!allowed.includes(key)) {
                add(path ? `${path}.${key}` : key, 'unexpected key — this field is not part of the schema');
            }
        });
    };

    /** Required non-empty string, e.g. pydantic's `Field(min_length=1)`. */
    const checkRequiredString = (node, key, path) => {
        const value = node[key];
        if (value === undefined || value === null || value === '') {
            add(`${path}.${key}`, 'this field is required');
        } else if (typeof value !== 'string') {
            add(`${path}.${key}`, 'must be a string');
        }
    };

    /** Optional string — present but non-string is still a violation. */
    const checkOptionalString = (node, key, path) => {
        const value = node[key];
        if (value !== undefined && value !== null && typeof value !== 'string') {
            add(`${path}.${key}`, 'must be a string');
        }
    };

    /** Optional list of strings; absent is fine since the service defaults to []. */
    const checkStringList = (node, key, path) => {
        const value = node[key];
        if (value === undefined || value === null) return;
        if (!Array.isArray(value)) {
            add(`${path}.${key}`, 'must be a list');
            return;
        }
        value.forEach((item, i) => {
            if (typeof item !== 'string') add(`${path}.${key}.${i}`, 'must be a string');
        });
    };

    /**
     * Walk an optional list of child nodes. Returns the entries actually worth
     * recursing into, so a malformed element is reported once rather than
     * cascading errors from every field inside it.
     */
    const eachChild = (node, key, path, cb) => {
        const value = node[key];
        // `path` is "" at the root, so join conditionally — the service reports
        // top-level paths as `domains.0.name`, not `.domains.0.name`, and the
        // two have to match for its errors and ours to render the same way.
        const listPath = path ? `${path}.${key}` : key;
        if (value === undefined || value === null) return;
        if (!Array.isArray(value)) {
            add(listPath, 'must be a list');
            return;
        }
        value.forEach((item, i) => {
            const childPath = `${listPath}.${i}`;
            if (!isPlainObject(item)) {
                add(childPath, 'must be a mapping');
                return;
            }
            cb(item, childPath);
        });
    };

    checkExtraKeys(data, ALLOWED_KEYS.root, '');
    checkRequiredString(data, 'version', '<root>');

    if (!Array.isArray(data.domains)) {
        add('domains', 'this field is required and must be a list');
    } else if (data.domains.length === 0) {
        add('domains', 'at least one domain is required');
    }

    eachChild(data, 'domains', '', (domain, domainPath) => {
        checkExtraKeys(domain, ALLOWED_KEYS.domain, domainPath);
        checkRequiredString(domain, 'name', domainPath);
        checkOptionalString(domain, 'description', domainPath);
        checkStringList(domain, 'synonyms', domainPath);

        eachChild(domain, 'datasets', domainPath, (dataset, datasetPath) => {
            checkExtraKeys(dataset, ALLOWED_KEYS.dataset, datasetPath);
            checkRequiredString(dataset, 'name', datasetPath);
            checkOptionalString(dataset, 'description', datasetPath);
            checkStringList(dataset, 'synonyms', datasetPath);
            checkStringList(dataset, 'tags', datasetPath);

            eachChild(dataset, 'fields', datasetPath, (field, fieldPath) => {
                checkExtraKeys(field, ALLOWED_KEYS.field, fieldPath);
                checkRequiredString(field, 'name', fieldPath);
                checkStringList(field, 'synonyms', fieldPath);
                checkStringList(field, 'tags', fieldPath);

                eachChild(field, 'value_groups', fieldPath, (vg, vgPath) => {
                    checkExtraKeys(vg, ALLOWED_KEYS.valueGroup, vgPath);
                    checkRequiredString(vg, 'business_term', vgPath);
                    checkStringList(vg, 'synonyms', vgPath);
                    checkRequiredString(vg, 'sql', vgPath);
                });
            });

            eachChild(dataset, 'joins', datasetPath, (join, joinPath) => {
                checkExtraKeys(join, ALLOWED_KEYS.join, joinPath);
                checkRequiredString(join, 'target_entity', joinPath);
                checkRequiredString(join, 'sql', joinPath);
                if (!JOIN_RELATIONS.includes(join.relation)) {
                    add(`${joinPath}.relation`, `must be one of: ${JOIN_RELATIONS.join(', ')}`);
                }
            });
        });

        eachChild(domain, 'metrics', domainPath, (metric, metricPath) => {
            checkExtraKeys(metric, ALLOWED_KEYS.metric, metricPath);
            checkRequiredString(metric, 'name', metricPath);
            checkOptionalString(metric, 'description', metricPath);
            checkStringList(metric, 'synonyms', metricPath);
            checkRequiredString(metric, 'sql', metricPath);
        });
    });

    return errors;
}

/**
 * Parse and validate in one step.
 *
 * @param {string} text
 * @returns {{data: ?Object, errors: Array<{path: string, message: string}>, syntaxError: boolean}}
 *          `syntaxError` distinguishes "this isn't YAML" from "this is YAML but
 *          the wrong shape" — the two need different UI treatment.
 */
export function parseAndValidate(text) {
    if (!text || !text.trim()) {
        return {
            data: null,
            errors: [{ path: '<root>', message: 'the context layer is empty' }],
            syntaxError: false,
        };
    }

    let data;
    try {
        data = YAML.parse(text);
    } catch (e) {
        // yaml puts the 1-based line/col on `linePos`; surface it so the message
        // points somewhere useful.
        const line = e?.linePos?.[0]?.line;
        return {
            data: null,
            errors: [{
                path: line ? `line ${line}` : 'YAML',
                message: (e?.message || 'could not parse YAML').split('\n')[0],
            }],
            syntaxError: true,
        };
    }

    return { data, errors: validateContextLayer(data), syntaxError: false };
}

/**
 * Serialize a document edited in Easy Mode back to YAML.
 *
 * Drops empty optional fields so a round trip through Easy Mode doesn't litter
 * the document with `description: ""` and `tags: []` on every node.
 */
export function stringifyContextLayer(data) {
    return YAML.stringify(pruneEmpty(data), { lineWidth: 0 });
}

/**
 * Identity keys for the nodes Easy Mode can reorder.
 *
 * Comments are grafted back by *identity* rather than by array index: Easy Mode
 * mostly adds and removes rows, and an index-keyed graft would slide every
 * comment up one node the moment a domain in the middle is deleted. Each list
 * is keyed on the field the user thinks of as the node's name, so a comment
 * follows the dataset it describes wherever that dataset ends up.
 */
const IDENTITY_KEYS = {
    domains: 'name',
    datasets: 'name',
    fields: 'name',
    metrics: 'name',
    value_groups: 'business_term',
    joins: 'target_entity',
};

/**
 * Path segment for one element of a list.
 *
 * Falls back to the index when the identity field is blank or duplicated —
 * a half-typed row has no stable name yet, and `#3` at least keeps it from
 * colliding with another nameless row in the same list.
 */
const identitySegment = (listKey, item, index, seen) => {
    const idKey = IDENTITY_KEYS[listKey];
    const raw = idKey && isPlainObject(item) ? item[idKey] : undefined;
    if (typeof raw !== 'string' || raw === '') return `#${index}`;
    // A duplicate name is invalid per the service, but it can exist mid-edit;
    // disambiguate so two same-named rows don't share one comment.
    const count = seen.get(raw) || 0;
    seen.set(raw, count + 1);
    return count === 0 ? raw : `${raw}~${count}`;
};

/**
 * Walk a `yaml` AST, invoking `visit(path, node)` for every mapping node.
 *
 * Paths are identity-based (`domains/web_analytics/datasets/pageviews`) so the
 * same node in the pre-edit and post-edit documents resolves to the same key.
 */
function walkMappings(node, path, visit) {
    if (!YAML.isMap(node)) return;
    visit(path, node);

    node.items.forEach((pair) => {
        const key = pair.key?.value;
        const value = pair.value;
        if (typeof key !== 'string') return;

        if (YAML.isSeq(value) && IDENTITY_KEYS[key]) {
            const seen = new Map();
            value.items.forEach((item, i) => {
                if (!YAML.isMap(item)) return;
                // `toJSON()` on the item would be wasteful here; read the one
                // identity scalar straight off the AST instead.
                const idKey = IDENTITY_KEYS[key];
                const idNode = item.get(idKey, true);
                const plain = { [idKey]: YAML.isScalar(idNode) ? idNode.value : undefined };
                walkMappings(item, `${path}${path ? '/' : ''}${key}/${identitySegment(key, plain, i, seen)}`, visit);
            });
        }
    });
}

/**
 * Separator for the composite comment keys below. A NUL can't occur in a YAML
 * key or in an identity path, so it can't collide with real content the way a
 * space or a dot could.
 */
const KEY_SEP = String.fromCharCode(0);

/** The comment fields `yaml` hangs off a node, plus the blank-line flag. */
const COMMENT_FIELDS = ['commentBefore', 'comment', 'spaceBefore'];

/**
 * Identity of a sequence's first item, or `null` if there isn't a usable one.
 *
 * A comment written above the *first* item of a list is parsed as the
 * sequence's own `commentBefore`, not the item's — so preserving it as a
 * property of the list would re-render it above whatever item happens to be
 * first after an edit, sliding `# our main analytics domain` onto a different
 * domain the moment the original one is deleted or reordered. Keying it to the
 * first item's identity instead lets the graft drop it when that item is no
 * longer leading.
 */
function firstItemIdentity(seqNode, listKey) {
    const first = seqNode?.items?.[0];
    if (!YAML.isMap(first)) return null;
    const idKey = IDENTITY_KEYS[listKey];
    const idNode = idKey ? first.get(idKey, true) : null;
    const raw = YAML.isScalar(idNode) ? idNode.value : undefined;
    return typeof raw === 'string' && raw !== '' ? raw : null;
}

/**
 * Collect every comment in `doc`, keyed by identity path plus the mapping key
 * it is attached to (`''` for comments on the node itself).
 */
function collectComments(doc) {
    const found = new Map();
    if (!doc || !YAML.isMap(doc.contents)) return found;

    walkMappings(doc.contents, '', (path, node) => {
        const record = (suffix, source) => {
            const entry = {};
            COMMENT_FIELDS.forEach((f) => {
                if (source[f] !== undefined && source[f] !== null && source[f] !== false) {
                    entry[f] = source[f];
                }
            });
            if (Object.keys(entry).length) found.set(`${path}${KEY_SEP}${suffix}`, entry);
        };

        record('', node);
        node.items.forEach((pair) => {
            const key = pair.key?.value;
            if (typeof key !== 'string') return;
            // A comment can sit on the key (`# note` above `name:`) or trail the
            // value (`name: x # note`); both are worth carrying over.
            if (pair.key) record(`k:${key}`, pair.key);
            if (!pair.value) return;

            // A list's own `commentBefore` really belongs to its first item;
            // re-key it so it cannot slide onto a different one.
            if (YAML.isSeq(pair.value) && IDENTITY_KEYS[key] && pair.value.commentBefore) {
                const owner = firstItemIdentity(pair.value, key);
                if (owner) {
                    found.set(`${path}${KEY_SEP}lead:${key}:${owner}`, {
                        commentBefore: pair.value.commentBefore,
                        ...(pair.value.spaceBefore ? { spaceBefore: true } : {}),
                    });
                    // Record the rest of the sequence's comments, minus the
                    // leading one now accounted for above.
                    const { commentBefore, ...rest } = pair.value;
                    record(`v:${key}`, rest);
                    return;
                }
            }
            record(`v:${key}`, pair.value);
        });
    });

    return found;
}

/** Reattach collected comments to whichever nodes still exist in `doc`. */
function applyComments(doc, found) {
    if (!found.size || !YAML.isMap(doc.contents)) return;

    walkMappings(doc.contents, '', (path, node) => {
        const restore = (suffix, target) => {
            const entry = found.get(`${path}${KEY_SEP}${suffix}`);
            if (!entry) return;
            COMMENT_FIELDS.forEach((f) => {
                if (entry[f] !== undefined) target[f] = entry[f];
            });
        };

        restore('', node);
        node.items.forEach((pair) => {
            const key = pair.key?.value;
            if (typeof key !== 'string') return;
            if (pair.key) restore(`k:${key}`, pair.key);
            if (!pair.value) return;
            restore(`v:${key}`, pair.value);

            // Put a leading list comment back on whichever node owns it. If the
            // owner is still first it goes back on the sequence, exactly where
            // it was; if the edit moved it down the list, it becomes that item's
            // own `commentBefore` so it travels with the node it describes
            // instead of being dropped or captured by the new first item.
            if (YAML.isSeq(pair.value) && IDENTITY_KEYS[key]) {
                const leadPrefix = `${path}${KEY_SEP}lead:${key}:`;
                const seen = new Map();
                pair.value.items.forEach((item, i) => {
                    if (!YAML.isMap(item)) return;
                    const idKey = IDENTITY_KEYS[key];
                    const idNode = item.get(idKey, true);
                    const raw = YAML.isScalar(idNode) ? idNode.value : undefined;
                    if (typeof raw !== 'string' || raw === '') return;
                    // Only the first occurrence of a name owns the comment;
                    // `identitySegment` disambiguates duplicates the same way.
                    const count = seen.get(raw) || 0;
                    seen.set(raw, count + 1);
                    if (count !== 0) return;

                    const entry = found.get(`${leadPrefix}${raw}`);
                    if (!entry) return;
                    const target = i === 0 ? pair.value : item;
                    COMMENT_FIELDS.forEach((f) => {
                        if (entry[f] !== undefined) target[f] = entry[f];
                    });
                });
            }
        });
    });
}

/**
 * Serialize an Easy Mode edit while keeping the comments from `previousText`.
 *
 * Easy Mode edits a plain JS object, and `YAML.parse` drops comments on the way
 * in — so re-serializing that object alone silently strips every comment the
 * user hand-wrote. Instead the pre-edit text is re-parsed into a comment-bearing
 * AST, its comments are indexed by identity path, and any whose node survived
 * the edit are grafted onto the freshly built document.
 *
 * What this does not preserve is hand-authored *formatting* — flow-style lists,
 * quote style and blank-line placement all normalize to whatever `YAML.stringify`
 * emits. Preserving those too would mean mutating the original AST in place
 * rather than rebuilding it, which the builder's whole-document change contract
 * does not currently allow.
 *
 * @param {Object} data - the edited document, as a plain object
 * @param {string} previousText - the YAML this edit was made against
 * @returns {string}
 */
export function stringifyPreservingComments(data, previousText) {
    const pruned = pruneEmpty(data);
    if (!previousText || !previousText.trim()) return YAML.stringify(pruned, { lineWidth: 0 });

    let previous;
    try {
        previous = YAML.parseDocument(previousText);
    } catch (e) {
        // Unparseable previous text has no comments to recover; the plain
        // serialization is still correct.
        return YAML.stringify(pruned, { lineWidth: 0 });
    }
    // `parseDocument` reports syntax errors rather than throwing.
    if (previous.errors?.length) return YAML.stringify(pruned, { lineWidth: 0 });

    const next = new YAML.Document(pruned);

    // The file header (`# PAICE context layer`) hangs off the document, not off
    // any node, so it has to be carried across separately.
    if (previous.commentBefore) next.commentBefore = previous.commentBefore;
    if (previous.comment) next.comment = previous.comment;

    applyComments(next, collectComments(previous));

    return next.toString({ lineWidth: 0 });
}

/** Strip empty strings and empty lists from optional keys, recursively. */
function pruneEmpty(node) {
    if (Array.isArray(node)) return node.map(pruneEmpty);
    if (!isPlainObject(node)) return node;

    const out = {};
    Object.entries(node).forEach(([key, value]) => {
        // `version`, `name` and the other required keys are kept even when
        // blank: an incomplete draft should serialize to YAML that fails
        // validation loudly rather than to YAML that's missing a key.
        const required = ['version', 'name', 'business_term', 'target_entity', 'relation', 'sql'];
        if (required.includes(key)) {
            out[key] = value;
            return;
        }
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value) && value.length === 0) return;
        out[key] = pruneEmpty(value);
    });
    return out;
}

/** Byte length of the document, for the size cap the service enforces. */
export function byteLength(text) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text || '').length;
    return unescape(encodeURIComponent(text || '')).length;
}

/**
 * Turn the agents service's 400 body into the same shape `validateContextLayer`
 * produces, so both client- and server-side failures render identically.
 */
export function normalizeServerErrors(detail) {
    if (!detail) return [];
    if (typeof detail === 'string') return [{ path: '', message: detail }];
    if (Array.isArray(detail.errors)) {
        return detail.errors.map((e) => ({
            path: e.path || '<root>',
            message: e.message || 'invalid value',
        }));
    }
    if (detail.error) {
        // YAML syntax failures come back as `{error, message}` with no per-field list.
        return [{ path: '', message: detail.message ? `${detail.error}: ${detail.message}` : detail.error }];
    }
    return [];
}

/** Counts for the summary strip, e.g. "3 domains · 12 datasets · 7 metrics". */
export function summarize(data) {
    const domains = Array.isArray(data?.domains) ? data.domains : [];
    let datasets = 0; let fields = 0; let metrics = 0; let joins = 0;
    domains.forEach((d) => {
        const ds = Array.isArray(d?.datasets) ? d.datasets : [];
        datasets += ds.length;
        metrics += Array.isArray(d?.metrics) ? d.metrics.length : 0;
        ds.forEach((s) => {
            fields += Array.isArray(s?.fields) ? s.fields.length : 0;
            joins += Array.isArray(s?.joins) ? s.joins.length : 0;
        });
    });
    return { domains: domains.length, datasets, fields, metrics, joins };
}
